// Data access for the app. Calls the shared db client (single source of truth)
// against the real API, or returns fixtures in ?mock mode. Dashboard/derived
// computations reuse the exact shared functions the Svelte app uses, so the two
// frontends can't diverge on what a number means.
import { computeAggregates, type Aggregates } from '@lib/dashboardData';
import { computeMonthlyBudget, type CategoryBudgetItem } from '@lib/budget';
import * as db from '@lib/db';
import {
	getTransactionsForMonth,
	getBudgetForMonth,
	getCategories,
	getRulesForCategory,
	getDebts,
	getDebt,
	getAccounts,
	getDebtPaymentSuggestion,
	type DebtView,
	type Budget,
	type Category,
	type Transaction,
	type Rule,
	type Account,
	type PaymentSuggestion,
} from '@lib/db';
import { buildSearchText, type TransactionView } from '@lib/transactionFilter';
import {
	mockTransactions,
	mockBudget,
	mockCloneSourceBudget,
	mockDebts,
	mockCategories,
	mockPaymentSuggestion,
	mockAddReconcile,
	mockRemoveReconcile,
	mockState,
} from './mock';

export const MOCK = new URLSearchParams(location.search).has('mock');

export interface DashboardData {
	aggregates: Aggregates;
	budget: CategoryBudgetItem[];
}

function budgetItems(
	transactions: ReturnType<typeof mockTransactions>,
	debts: DebtView[],
	categoryBudgets: Record<string, number>
): CategoryBudgetItem[] {
	return computeMonthlyBudget({
		transactions,
		debts,
		categoryBudgets,
		isNewBudget: false,
		mode: 'spent-or-budgeted',
	}).filter((b) => b.budgeted > 0 || b.spent > 0);
}

export async function loadDashboard(month: string): Promise<DashboardData> {
	if (MOCK) {
		const txs = mockTransactions();
		const debts = mockDebts();
		return { aggregates: computeAggregates(txs), budget: budgetItems(txs, debts, mockBudget().categoryBudgets ?? {}) };
	}
	const [txs, bdg, debts] = await Promise.all([
		getTransactionsForMonth(month),
		getBudgetForMonth(month),
		getDebts(),
	]);
	return {
		aggregates: computeAggregates(txs),
		budget: budgetItems(txs, debts, bdg?.categoryBudgets ?? {}),
	};
}

export async function loadDebt(id: string): Promise<DebtView> {
	if (MOCK) return mockDebts().find((d) => d.id === id) ?? mockDebts()[0];
	return getDebt(id);
}

export async function loadMonthTransactions(month: string): Promise<Transaction[]> {
	if (MOCK) return mockTransactions();
	return getTransactionsForMonth(month);
}

export async function loadDebts(): Promise<DebtView[]> {
	if (MOCK) return mockDebts();
	return getDebts();
}

export interface DebtsListData {
	debts: DebtView[];
	categories: Category[];
	accounts: Account[];
}

export async function loadDebtsListData(): Promise<DebtsListData> {
	if (MOCK) return { debts: mockDebts(), categories: mockCategories(), accounts: [] };
	const [debts, categories, accounts] = await Promise.all([getDebts(), getCategories(), getAccounts()]);
	return { debts, categories, accounts };
}

export interface BudgetData {
	categories: Category[];
	budget: Budget | null;
	transactions: Transaction[];
	debts: DebtView[];
}

export async function loadBudgetData(month: string): Promise<BudgetData> {
	if (MOCK) {
		if (mockState.blankBudget) {
			return { categories: mockCategories(), budget: null, transactions: [], debts: [] };
		}
		return {
			categories: mockCategories(),
			budget: mockBudget(),
			transactions: mockTransactions(),
			debts: mockDebts(),
		};
	}
	const [categories, budget, transactions, debts] = await Promise.all([
		getCategories(),
		getBudgetForMonth(month),
		getTransactionsForMonth(month),
		getDebts(),
	]);
	return { categories, budget, transactions, debts };
}

// For the clone-previous-budget feature: fetch a specific month's budget.
export async function loadBudgetForMonth(month: string): Promise<Budget | null> {
	if (MOCK) return mockState.blankBudget ? mockCloneSourceBudget() : null;
	return getBudgetForMonth(month);
}

export interface TxData {
	transactions: TransactionView[];
	categories: Category[];
}

export async function loadTransactionsData(month: string): Promise<TxData> {
	const withSearch = (raw: Transaction[]): TransactionView[] =>
		raw.map((t) => ({ ...t, searchText: buildSearchText(t) }));
	if (MOCK) {
		return { transactions: withSearch(mockTransactions()), categories: mockCategories() };
	}
	const [raw, categories] = await Promise.all([getTransactionsForMonth(month), getCategories()]);
	return { transactions: withSearch(raw), categories };
}

export async function loadCategories(): Promise<Category[]> {
	if (MOCK) return mockCategories();
	return getCategories();
}

export async function loadRules(name: string): Promise<Rule[]> {
	if (MOCK) return [];
	return getRulesForCategory(name);
}

// Mock-aware so the smoke can exercise the reconcile editor (the suggestion
// drives the allocation defaults the validator gates Save on).
export async function loadPaymentSuggestion(debtId: string, txPk: string, txSk: string): Promise<PaymentSuggestion> {
	if (MOCK) return mockPaymentSuggestion();
	return getDebtPaymentSuggestion(debtId, txPk, txSk);
}

// ------------------------------------------------------------------ writes --
// Mock-aware write layer: in ?mock mode writes resolve locally (echoing a
// plausible result, with just enough statefulness for reload-style flows) so
// the jsdom smoke can click through save/undo/optimistic paths without a
// backend. Deployed (no ?mock) each is the shared db client function
// unchanged. Routes import writes from here, never from @lib/db directly.
function mockable<A extends unknown[], R>(
	real: (...args: A) => Promise<R>,
	mock: (...args: A) => R
): (...args: A) => Promise<R> {
	return (...args: A) => (MOCK ? Promise.resolve(mock(...args)) : real(...args));
}

let mockSeq = 0;

export const updateTransactionCategory = mockable(db.updateTransactionCategory, () => undefined);
export const setTransactionExcludeFromBudget = mockable(db.setTransactionExcludeFromBudget, () => undefined);
export const setTransactionMonthOffset = mockable(db.setTransactionMonthOffset, () => undefined);
export const setTransactionSplits = mockable(db.setTransactionSplits, () => undefined);
export const clearTransactionSplits = mockable(db.clearTransactionSplits, () => undefined);
export const saveBudget = mockable(db.saveBudget, () => undefined);
export const createCategory = mockable(db.createCategory, (name, _sortOrder, type) => ({ name, type }) as Category);
export const updateCategory = mockable(db.updateCategory, () => undefined);
export const deleteCategory = mockable(db.deleteCategory, () => undefined);
export const createRule = mockable(
	db.createRule,
	(categoryName, rule) =>
		({ pk: `CAT#${categoryName}`, sk: `RULE#mock-${mockSeq++}`, pattern: rule.pattern, matchType: rule.matchType ?? 'regex', amountSign: rule.amountSign ?? '' }) as unknown as Rule
);
export const deleteRule = mockable(db.deleteRule, () => undefined);
export const createDebt = mockable(db.createDebt, (debt) => ({ ...debt, id: `mock-debt-${mockSeq++}` }) as Awaited<ReturnType<typeof db.createDebt>>);
export const updateDebt = mockable(db.updateDebt, (id, updates) => ({ id, ...updates }) as Awaited<ReturnType<typeof db.updateDebt>>);
export const deleteDebt = mockable(db.deleteDebt, () => undefined);
export const reconcilePayment = mockable(db.reconcilePayment, () => undefined);
export const unreconcilePayment = mockable(db.unreconcilePayment, () => undefined);
export const reconcileBalance = mockable(db.reconcileBalance, (_id, balance, opts) => mockAddReconcile(balance, opts));
export const deleteReconcile = mockable(db.deleteReconcile, (_id, date) => mockRemoveReconcile(date));
export const saveValuation = mockable(db.saveValuation, () => []);
export const deleteValuation = mockable(db.deleteValuation, () => undefined);
export const uploadStatement = mockable(db.uploadStatement, (_id, file) => ({ key: `mock/${file.name}`, name: file.name }));
export const getStatementDownloadUrl = mockable(db.getStatementDownloadUrl, () => 'about:blank');
